import {
   Badge,
   Button,
   Container,
   Center,
   Code,
   CopyButton,
   Divider,
   Grid,
   Group,
   Modal,
   Paper,
   PinInput,
   Stack,
   Text,
   ThemeIcon,
   Title,
   TextInput,
   Alert,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import {
   IconArrowLeft,
   IconCreditCard,
   IconUser,
   IconWallet,
   IconBuildingBank,
   IconCurrencyNaira,
   IconCalendar,
   IconAlertCircle,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useState, ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Endpoints } from "../../lib/endpoints";
import { usePerformRequest, PerformRequest } from "../../lib/usePerformRequest";
import { NonPaginatedResponse } from "../../types";
import { ILoanRequestResponseProps } from "../../types/Loan";
import { HttpCodes } from "../../lib/data";
import { useToastNotification } from "../../hooks/useToastNotification";

const formatCurrency = (amount: number) => `₦${amount.toLocaleString("en-NG", { minimumFractionDigits: 0 })}`;

const formatDate = (timestamp?: number) =>
   timestamp
      ? new Date(timestamp).toLocaleString("en-US", {
           year: "numeric",
           month: "short",
           day: "numeric",
           hour: "2-digit",
           minute: "2-digit",
        })
      : "N/A";

const InfoRow = ({ label, value }: { label: string; value?: ReactNode }) => (
   <Group justify="space-between" align="flex-start">
      <Text size="sm" fw={600} c="dimmed">
         {label}
      </Text>
      <Text size="sm" fw={500} ta="right" style={{ maxWidth: "60%" }}>
         {value ?? "N/A"}
      </Text>
   </Group>
);

export default function LoanRequestDetails() {
   const { requestId } = useParams<{ requestId: string }>();
   const navigate = useNavigate();
   const toast = useToastNotification();

   const [directDebitAmount, setDirectDebitAmount] = useState("");
   const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
   const [isSettingDirectDebit, setIsSettingDirectDebit] = useState(false);

   const [disburseModalOpen, setDisburseModalOpen] = useState(false);
   const [disbursementAmount, setDisbursementAmount] = useState("");
   const [otp, setOtp] = useState("");
   const [isDisbursing, setIsDisbursing] = useState(false);
   const [requiresOtp, setRequiresOtp] = useState(false);
   const [transferCode, setTransferCode] = useState("");

   const {
      data: loanResponse,
      isLoading,
      reloadData,
   } = usePerformRequest<NonPaginatedResponse<ILoanRequestResponseProps>>({
      method: Endpoints.GetSingleLoanRequest(requestId || "").method,
      route: requestId ? Endpoints.GetSingleLoanRequest(requestId).route : "",
   });

   const loan = loanResponse?.data;
   const handleDisburseFromDetails = async () => {
      if (!loan || !requestId) return;

      if (requiresOtp) {
         const trimmedOtp = otp.trim();
         if (!transferCode) {
            toast.error("Missing transfer code. Please restart the disbursement flow.");
            return;
         }
         if (!trimmedOtp || trimmedOtp.length !== 6) {
            toast.error("Enter the 6-digit Paystack OTP to finalize this transfer.");
            return;
         }
      }

      const raw = disbursementAmount.replace(/,/g, "").trim();
      const value = Number(raw);

      if (!raw || isNaN(value) || value <= 0) {
         toast.error("Enter a valid disbursement amount greater than zero.");
         return;
      }

      if (value > loan.amount) {
         toast.error(`Disbursement amount cannot exceed approved principal of ${formatCurrency(loan.amount)}.`);
         return;
      }

      setIsDisbursing(true);
      try {
         const payload: any = { amount: value };
         if (requiresOtp && transferCode) {
            payload.transferCode = transferCode;
         }
         if (otp.trim()) payload.otp = otp.trim();

         const response = await PerformRequest<{
            data?: {
               success?: boolean;
               requiresOtp?: boolean;
               transferCode?: string;
               message?: string;
            };
            message?: string;
         }>({
            method: Endpoints.DisburseLoan(requestId).method,
            route: Endpoints.DisburseLoan(requestId).route,
            data: payload,
         });

         if (response && response.status === HttpCodes.Success) {
            const apiEnvelope = response.data as any;
            const result = (apiEnvelope?.data ?? apiEnvelope) as
               | {
                    success?: boolean;
                    requiresOtp?: boolean;
                    transferCode?: string;
                    message?: string;
                 }
               | undefined;

            if (result?.requiresOtp && !otp.trim()) {
               setRequiresOtp(true);
               setTransferCode(result?.transferCode || "");
               toast.warning(
                  "OTP required. Check the Paystack disbursement account email/SMS or Paystack dashboard, then enter the OTP to finalize."
               );
               return;
            }

            if (result?.success) {
               toast.success(result.message || "Loan disbursed successfully!");
               await reloadData();
               setDisburseModalOpen(false);
               setDisbursementAmount("");
               setOtp("");
               setRequiresOtp(false);
               setTransferCode("");
            } else {
               toast.error(result?.message || "Transfer is being processed. Please check the status later.");
            }
         } else {
            toast.error(response?.message || "Failed to disburse loan");
         }
      } catch (err: any) {
         toast.error(err?.message || "Failed to disburse loan");
      } finally {
         setIsDisbursing(false);
      }
   };

   const handleSetDirectDebit = async () => {
      if (!loan || !requestId) return;

      const raw = directDebitAmount.replace(/,/g, "").trim();
      const value = Number(raw);
      if (!raw || isNaN(value) || value <= 0) {
         toast.error("Enter a valid direct debit amount greater than zero.");
         return;
      }

      // Validate scheduled date if provided
      if (scheduledDate) {
         const now = new Date();
         now.setHours(0, 0, 0, 0);
         const selectedDate = new Date(scheduledDate);
         selectedDate.setHours(0, 0, 0, 0);

         if (selectedDate < now) {
            toast.error("Scheduled date cannot be in the past.");
            return;
         }
      }

      setIsSettingDirectDebit(true);
      try {
         const requestData: { amount: number; scheduledDate?: number } = { amount: value };

         // Include scheduled date if provided
         if (scheduledDate) {
            requestData.scheduledDate = scheduledDate.getTime();
         }

         const response = await PerformRequest<{
            scheduledDirectDebitAmount: number;
            scheduledDirectDebitAt?: number;
            outstandingBalance: number;
         }>({
            method: Endpoints.SetLoanDirectDebitAmount(requestId).method,
            route: Endpoints.SetLoanDirectDebitAmount(requestId).route,
            data: requestData,
         });

         if (response && response.status === HttpCodes.Success) {
            const result = response.data as any;
            const dateText = result?.scheduledDirectDebitAt
               ? ` on ${formatDate(result.scheduledDirectDebitAt)}`
               : " immediately";
            toast.success(
               `Direct debit of ${formatCurrency(
                  result?.scheduledDirectDebitAmount ?? value
               )} has been scheduled${dateText}.`
            );
            // Clear form
            setDirectDebitAmount("");
            setScheduledDate(null);
         } else {
            toast.error(response?.message || "Failed to set direct debit amount.");
         }
      } catch (err: any) {
         toast.error(err?.message || "Failed to set direct debit amount.");
      } finally {
         setIsSettingDirectDebit(false);
      }
   };

   if (isLoading || !loan) {
      return (
         <Container size="xl" py="xl">
            <Paper p="xl" radius="sm" withBorder>
               <Text>Loading loan details...</Text>
            </Paper>
         </Container>
      );
   }

   const user = loan.userID as any;
   const loanProduct = loan.loanID as any;

   const principal = loan.amount;
   const interestRate = loanProduct?.interestRate || 0;
   const duration = loan.duration;
   const totalInterest = (principal * interestRate * duration) / 100;
   const totalDue = principal + totalInterest;

   return (
      <Container size="xl" py="xl">
         <Modal
            opened={disburseModalOpen}
            onClose={() => {
               if (isDisbursing) return;
               setDisburseModalOpen(false);
               setDisbursementAmount("");
               setOtp("");
               setRequiresOtp(false);
               setTransferCode("");
            }}
            title={
               <Group gap="sm">
                  <ThemeIcon
                     size="lg"
                     radius="md"
                     style={{ background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)" }}
                  >
                     <IconWallet size={22} />
                  </ThemeIcon>
                  <div>
                     <Text size="xl" fw={700}>
                        Disburse Loan Funds
                     </Text>
                     <Text size="sm" c="dimmed">
                        Send funds to the customer's bank account
                     </Text>
                  </div>
               </Group>
            }
            centered
            size="lg"
         >
            <Stack gap="md">
               <Alert icon={<IconAlertCircle size={18} />} title="Important Reminder" color="orange" variant="light">
                  Before disbursing this loan please check that you have funded the Disbursement Account at Paystack.
               </Alert>

               <Paper p="md" radius="md" withBorder>
                  <Stack gap={4}>
                     <Text size="sm" c="dimmed">
                        Approved Principal
                     </Text>
                     <Text size="lg" fw={700}>
                        {formatCurrency(loan.amount)}
                     </Text>
                  </Stack>
               </Paper>

               <TextInput
                  label="Disbursement Amount"
                  placeholder="Enter amount to disburse"
                  leftSection={<IconCurrencyNaira size={16} />}
                  value={disbursementAmount}
                  onChange={(e) => setDisbursementAmount(e.currentTarget.value)}
                  disabled={requiresOtp}
               />

               {requiresOtp && (
                  <Stack gap="xs">
                     <Alert icon={<IconAlertCircle size={18} />} title="OTP Required" color="blue" variant="light">
                        Paystack has created the transfer but requires an OTP to authorize it. The OTP is sent to your
                        Paystack <strong>business/disbursement</strong> account owner phone/email (or available in the
                        Paystack dashboard).
                     </Alert>

                     {transferCode && (
                        <Group justify="space-between" align="center">
                           <Text size="sm" fw={600} c="dimmed">
                              Transfer Code
                           </Text>
                           <Group gap="xs">
                              <Code>{transferCode}</Code>
                              <CopyButton value={transferCode}>
                                 {({ copied, copy }) => (
                                    <Button variant="subtle" size="xs" onClick={copy}>
                                       {copied ? "Copied" : "Copy"}
                                    </Button>
                                 )}
                              </CopyButton>
                           </Group>
                        </Group>
                     )}

                     <Center>
                        <PinInput
                           length={6}
                           size="md"
                           type="number"
                           oneTimeCode
                           autoFocus
                           value={otp}
                           onChange={setOtp}
                        />
                     </Center>
                  </Stack>
               )}

               <Group justify="flex-end" mt="md">
                  {requiresOtp && (
                     <Button
                        variant="subtle"
                        onClick={() => {
                           if (isDisbursing) return;
                           setRequiresOtp(false);
                           setOtp("");
                           setTransferCode("");
                        }}
                        disabled={isDisbursing}
                     >
                        Restart Disbursement
                     </Button>
                  )}
                  <Button
                     variant="outline"
                     color="gray"
                     onClick={() => {
                        if (isDisbursing) return;
                        setDisburseModalOpen(false);
                        setDisbursementAmount("");
                        setOtp("");
                        setRequiresOtp(false);
                        setTransferCode("");
                     }}
                     disabled={isDisbursing}
                  >
                     Cancel
                  </Button>
                  <Button
                     leftSection={<IconWallet size={18} />}
                     onClick={handleDisburseFromDetails}
                     loading={isDisbursing}
                     disabled={!disbursementAmount.trim() || !user?.bankAccount || !user?.bankAccount?.accountNumber}
                  >
                     {requiresOtp ? "Finalize Transfer with OTP" : "Disburse Funds"}
                  </Button>
               </Group>
            </Stack>
         </Modal>
         <Stack gap="xl">
            {/* Header + Direct Debit Action */}
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
               <Group justify="space-between" align="flex-start">
                  <div className="flex-col">
                     <Button
                        variant="subtle"
                        leftSection={<IconArrowLeft size={18} />}
                        onClick={() => navigate("/dashboard/loan-requests")}
                        style={{ color: "#679369" }}
                     >
                        Back to Loan Requests
                     </Button>
                     <Group gap="sm">
                        <ThemeIcon
                           size={40}
                           radius="md"
                           style={{
                              background: "linear-gradient(135deg, #699269 0%, #81C784 100%)",
                           }}
                        >
                           <IconCreditCard size={22} color="white" />
                        </ThemeIcon>
                        <div>
                           <Title order={2} fw={800} style={{ color: "#1F2937" }}>
                              Loan Request Details
                           </Title>
                           <Text size="sm" c="dimmed">
                              Full breakdown of loan, user profile, and repayment summary
                           </Text>
                        </div>
                     </Group>
                  </div>

                  {loan.status === "approved" && (
                     <Paper p="md" radius="md" withBorder style={{ background: "#F9FAFB", minWidth: 400 }}>
                        <Stack gap="sm">
                           <Text size="sm" fw={600} c="dimmed">
                              Schedule Auto Direct Debit
                           </Text>
                           <Stack gap="xs">
                              <TextInput
                                 placeholder="Amount to debit (₦)"
                                 value={directDebitAmount}
                                 onChange={(e) => setDirectDebitAmount(e.currentTarget.value)}
                                 leftSection={<IconCurrencyNaira size={16} />}
                                 label="Debit Amount"
                              />
                              <DatePickerInput
                                 placeholder="Select date (optional)"
                                 value={scheduledDate}
                                 onChange={(value) => setScheduledDate(value as unknown as Date | null)}
                                 leftSection={<IconCalendar size={16} />}
                                 label="Scheduled Date"
                                 description="Leave empty to schedule immediately"
                                 minDate={new Date()}
                                 clearable
                              />
                           </Stack>
                           {loan.scheduledDirectDebitAmount && loan.scheduledDirectDebitAt && (
                              <Paper p="xs" radius="sm" style={{ background: "#EFF6FF", border: "1px solid #BFDBFE" }}>
                                 <Text size="xs" c="blue">
                                    <strong>Scheduled:</strong> {formatCurrency(loan.scheduledDirectDebitAmount)} on{" "}
                                    {formatDate(loan.scheduledDirectDebitAt)}
                                 </Text>
                              </Paper>
                           )}
                           <Button
                              leftSection={<IconWallet size={18} />}
                              onClick={handleSetDirectDebit}
                              loading={isSettingDirectDebit}
                              disabled={
                                 !directDebitAmount.trim() ||
                                 !user?.bankAccount ||
                                 !user?.bankAccount?.accountNumber ||
                                 !user?.isDirectDebitMandateActive
                              }
                              fullWidth
                           >
                              {scheduledDate ? "Schedule Direct Debit" : "Set Direct Debit"}
                           </Button>
                           <Button
                              variant="gradient"
                              gradient={{ from: "#3B82F6", to: "#2563EB", deg: 135 }}
                              leftSection={<IconWallet size={18} />}
                              onClick={() => setDisburseModalOpen(true)}
                              disabled={!user?.bankAccount || !user?.bankAccount?.accountNumber}
                           >
                              Disburse Funds
                           </Button>
                           {(!user?.bankAccount || !user?.bankAccount?.accountNumber) && (
                              <Text size="xs" c="red">
                                 User must have a bank account set up
                              </Text>
                           )}
                           {user?.bankAccount && !user?.isDirectDebitMandateActive && (
                              <Text size="xs" c="orange">
                                 User must complete direct debit authorization
                              </Text>
                           )}
                        </Stack>
                     </Paper>
                  )}
               </Group>
            </motion.div>

            {/* Top Status & Summary Cards */}
            <Grid gutter="lg">
               <Grid.Col span={{ base: 12, md: 4 }}>
                  <Paper
                     p="lg"
                     radius="md"
                     withBorder
                     style={{
                        background: "linear-gradient(135deg, #ECFDF3 0%, #D1FAE5 100%)",
                        borderColor: "#22C55E",
                     }}
                  >
                     <Stack gap="xs">
                        <Text size="xs" c="dimmed" fw={600}>
                           Status
                        </Text>
                        <Group gap="xs">
                           <Badge color="green" size="lg" radius="sm">
                              {loan.status.toUpperCase()}
                           </Badge>
                        </Group>
                        <Text size="xs" c="dimmed">
                           Applied on {formatDate(loan.createdAt)}
                        </Text>
                     </Stack>
                  </Paper>
               </Grid.Col>

               <Grid.Col span={{ base: 12, md: 4 }}>
                  <Paper p="lg" radius="md" withBorder>
                     <Group gap="sm">
                        <ThemeIcon size={36} radius="md" color="green" variant="light">
                           <IconCurrencyNaira size={20} />
                        </ThemeIcon>
                        <Stack gap={2}>
                           <Text size="xs" c="dimmed" fw={500}>
                              Principal Amount
                           </Text>
                           <Text size="lg" fw={700}>
                              {formatCurrency(principal)}
                           </Text>
                        </Stack>
                     </Group>
                  </Paper>
               </Grid.Col>

               <Grid.Col span={{ base: 12, md: 4 }}>
                  <Paper p="lg" radius="md" withBorder>
                     <Stack gap={2}>
                        <Text size="xs" c="dimmed" fw={500}>
                           Total Due (Principal + Interest)
                        </Text>
                        <Text size="lg" fw={700}>
                           {formatCurrency(totalDue)}
                        </Text>
                        <Text size="xs" c="dimmed">
                           Duration: {duration} month{duration === 1 ? "" : "s"} • Rate: {interestRate}% / month
                        </Text>
                     </Stack>
                  </Paper>
               </Grid.Col>
            </Grid>

            {/* User + Loan Panels */}
            <Grid gutter="lg">
               <Grid.Col span={{ base: 12, md: 6 }}>
                  <Paper shadow="sm" p="xl" radius="sm" style={{ border: "2px solid #e5e7eb" }}>
                     <Stack gap="md">
                        <Group gap="xs">
                           <ThemeIcon size={42} radius="md" style={{ background: "#14532D" }}>
                              <IconUser size={22} color="white" />
                           </ThemeIcon>
                           <Title order={3} fw={700}>
                              User Details
                           </Title>
                        </Group>
                        <Divider />
                        <Stack gap="sm">
                           <InfoRow label="Email" value={user?.email} />
                           <InfoRow label="Phone" value={user?.phone} />
                           <InfoRow
                              label="Bank Account"
                              value={
                                 user?.bankAccount
                                    ? `${user.bankAccount.accountName} • ${user.bankAccount.accountNumber} (${
                                         user.bankAccount.bankName || user.bankAccount.bankCode
                                      })`
                                    : "Not set"
                              }
                           />
                        </Stack>
                     </Stack>
                  </Paper>
               </Grid.Col>

               <Grid.Col span={{ base: 12, md: 6 }}>
                  <Paper shadow="sm" p="xl" radius="sm" style={{ border: "2px solid #e5e7eb" }}>
                     <Stack gap="md">
                        <Group gap="xs">
                           <ThemeIcon size={42} radius="md" style={{ background: "#1D4ED8" }}>
                              <IconCreditCard size={22} color="white" />
                           </ThemeIcon>
                           <Title order={3} fw={700}>
                              Loan Details
                           </Title>
                        </Group>
                        <Divider />
                        <Stack gap="sm">
                           <InfoRow label="Product" value={loanProduct?.name} />
                           <InfoRow label="Amount Requested" value={formatCurrency(principal)} />
                           <InfoRow label="Duration" value={`${duration} month${duration === 1 ? "" : "s"}`} />
                           <InfoRow label="Interest Rate" value={`${interestRate}% per month`} />
                           <InfoRow label="Purpose" value={loan.purpose} />
                        </Stack>
                     </Stack>
                  </Paper>
               </Grid.Col>
            </Grid>
         </Stack>
      </Container>
   );
}
